import React, { useState } from 'react';
import { X, User, MapPin, AlertTriangle, FileText, Activity, Heart, Users, Shield } from 'lucide-react';
import { updateDoc, doc, Timestamp } from 'firebase/firestore';
import { BENEFICIARY_STATUS, STATUS_LABELS } from '../../constants/beneficiaryStatuses';

const EditClientModal = ({ client, db, onClose }) => {
  // Initialiser l'adresse comme un objet avec des propriétés par défaut
  const initialAddress = client?.address ? (
    typeof client.address === 'string'
      ? { street: client.address, city: '', postalCode: '', country: 'France' }
      : {
        street: client.address.street || '',
        city: client.address.city || '',
        postalCode: client.address.postalCode || '',
        country: client.address.country || 'France'
      }
  ) : { street: '', city: '', postalCode: '', country: 'France' };

  // Fonction utilitaire pour déterminer le statut initial
  const getInitialStatus = (c) => {
    if (!c) return BENEFICIARY_STATUS.ACTIVE;
    if (c.status) return c.status;
    if (c.isArchived) return BENEFICIARY_STATUS.ARCHIVED;
    if (c.isActive === false) return BENEFICIARY_STATUS.INACTIVE;
    return BENEFICIARY_STATUS.ACTIVE;
  };

  const [formData, setFormData] = useState({
    firstName: client?.firstName || '',
    lastName: client?.lastName || '',
    email: client?.email || '',
    phoneNumber: client?.phoneNumber || '',
    birthDate: client?.birthDate ? (
      client.birthDate.toDate ?
        client.birthDate.toDate().toISOString().split('T')[0] :
        new Date(client.birthDate).toISOString().split('T')[0]
    ) : '',
    address: initialAddress,
    status: getInitialStatus(client),
    typeEntite: client?.typeEntite || 'Particulier',
    color: client?.color || '#4ECDC4', // Add color initialized from client
    emergencyContact: client?.emergencyContact || '',
    additionalInfo: client?.additionalInfo || '',
    // Données d'évaluation enrichies
    autonomyAssessment: client?.autonomyAssessment || {
      autonomyScore: '',
      gir: '',
      needsHelp: {
        hygiene: false,
        nutrition: false,
        mobility: false,
        housekeeping: false,
        domestic: false
      }
    },
    healthSummary: client?.healthSummary || {
      generalHealth: '',
      mainPathologies: '',
      medicationCount: '',
      fallRisk: ''
    },
    emergencyContacts: client?.emergencyContacts || [
      { name: '', relationship: '', phone1: '', phone2: '' }
    ],
    familyCaregiver: client?.familyCaregiver || {
      name: '',
      relationship: '',
      frequency: '',
      burnoutLevel: ''
    },
    serviceNeeds: client?.serviceNeeds || {
      estimatedWeeklyHours: ''
    }
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Préparer les données pour Firestore
      const clientData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        typeEntite: formData.typeEntite,
        status: formData.status,
        color: formData.color, // Include color in update
        // Compatibilité
        isActive: formData.status === BENEFICIARY_STATUS.ACTIVE || formData.status === BENEFICIARY_STATUS.PENDING,
        isArchived: formData.status === BENEFICIARY_STATUS.ARCHIVED,
        emergencyContact: formData.emergencyContact,
        additionalInfo: formData.additionalInfo,
        updatedAt: Timestamp.now()
      };

      // Ajouter la date de naissance si elle existe
      if (formData.birthDate) {
        clientData.birthDate = Timestamp.fromDate(new Date(formData.birthDate));
      }

      await updateDoc(doc(db, 'clients', client.id), clientData);
      alert('Bénéficiaire modifié avec succès');
      onClose();
    } catch (error) {
      console.error('Erreur modification bénéficiaire:', error);
      alert('Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const handleAutonomyAssessmentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      autonomyAssessment: {
        ...prev.autonomyAssessment,
        [field]: value
      }
    }));
  };

  const handleHealthSummaryChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      healthSummary: {
        ...prev.healthSummary,
        [field]: value
      }
    }));
  };

  const handleFamilyCaregiverChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      familyCaregiver: {
        ...prev.familyCaregiver,
        [field]: value
      }
    }));
  };

  const handleServiceNeedsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      serviceNeeds: {
        ...prev.serviceNeeds,
        [field]: value
      }
    }));
  };

  const handleNeedsHelpChange = (domain, value) => {
    setFormData(prev => ({
      ...prev,
      autonomyAssessment: {
        ...prev.autonomyAssessment,
        needsHelp: {
          ...prev.autonomyAssessment.needsHelp,
          [domain]: value
        }
      }
    }));
  };

  const handleEmergencyContactChange = (index, field, value) => {
    const updatedContacts = [...formData.emergencyContacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      emergencyContacts: updatedContacts
    }));
  };

  const addEmergencyContact = () => {
    setFormData(prev => ({
      ...prev,
      emergencyContacts: [
        ...prev.emergencyContacts,
        { name: '', relationship: '', phone1: '', phone2: '' }
      ]
    }));
  };

  const removeEmergencyContact = (index) => {
    if (formData.emergencyContacts.length > 1) {
      const updatedContacts = formData.emergencyContacts.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        emergencyContacts: updatedContacts
      }));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête du modal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Modifier le bénéficiaire</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Mettez à jour les informations de {client?.firstName} {client?.lastName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Informations de base */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <User size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations de base
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Couleur du dossier</label>
                <div className="flex flex-wrap gap-3 mb-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                  {/* Palette étendue */}
                  {(() => {
                    const standardPalette = [
                      // Rouges / Roses
                      '#FF6B6B', '#F44336', '#E53935', '#D32F2F', '#E91E63', '#F06292', '#C2185B',
                      // Oranges / Jaunes
                      '#FFA07A', '#FFB74D', '#FF9800', '#F57C00', '#FFD54F', '#FFC107', '#FFA000',
                      // Verts / Turquoises
                      '#4ECDC4', '#26A69A', '#009688', '#00796B', '#AED581', '#8BC34A', '#689F38',
                      // Bleus
                      '#45B7D1', '#4FC3F7', '#03A9F4', '#0288D1', '#3F51B5', '#7986CB', '#303F9F',
                      // Violets
                      '#9575CD', '#BA68C8', '#9C27B0', '#7B1FA2', '#673AB7', '#512DA8',
                      // Gris / Neutres
                      '#90A4AE', '#78909C', '#607D8B', '#546E7A', '#455A64', '#37474F'
                    ];

                    // Si une couleur personnalisée existe et n'est pas dans la palette, on l'ajoute au début
                    const displayColors = formData.color && !standardPalette.includes(formData.color)
                      ? [formData.color, ...standardPalette]
                      : standardPalette;

                    return displayColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => handleChange('color', color)}
                        className={`w-10 h-10 rounded-full transition-all duration-200 flex items-center justify-center ${formData.color === color ? 'ring-4 ring-offset-2 ring-blue-500/50 scale-110 shadow-lg' : 'hover:scale-110 hover:shadow-md'}`}
                        style={{ backgroundColor: color }}
                        title={color}
                      >
                        {formData.color === color && <span className="text-white font-bold text-sm drop-shadow-md">✓</span>}
                      </button>
                    ));
                  })()}

                  {/* Custom color input fallback */}
                  <div className="relative">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => handleChange('color', e.target.value)}
                      className="w-10 h-10 p-0 border-0 rounded-full overflow-hidden cursor-pointer opacity-0 absolute inset-0"
                    />
                    <div className="w-10 h-10 rounded-full border-2 border-dashed border-gray-400 flex items-center justify-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer" title="Couleur personnalisée">
                      <span className="text-xl">+</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénom</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleChange('phoneNumber', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de naissance</label>
                <input
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => handleChange('birthDate', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type d'entité</label>
                <select
                  value={formData.typeEntite}
                  onChange={(e) => handleChange('typeEntite', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Particulier">Particulier</option>
                  <option value="Entreprise">Entreprise</option>
                  <option value="Association">Association</option>
                  <option value="Collectivité">Collectivité</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Statut</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.values(BENEFICIARY_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <MapPin size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Adresse
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rue</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Numéro et nom de rue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code postal</label>
                <input
                  type="text"
                  value={formData.address.postalCode}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="75000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ville</label>
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Paris"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pays</label>
                <input
                  type="text"
                  value={formData.address.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="France"
                />
              </div>
            </div>
          </div>

          {/* Informations médicales - Uniquement via questionnaire */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <AlertTriangle size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations médicales
            </h4>
            <div className="text-center py-6">
              <AlertTriangle size={48} className="mx-auto text-blue-500 dark:text-blue-400 mb-4" />
              <h5 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-2">
                Questionnaire d'évaluation requis
              </h5>
              <p className="text-blue-600 dark:text-blue-400 mb-4">
                Les informations médicales (allergies, traitements, niveau d'autonomie) ne sont disponibles
                que via le questionnaire d'évaluation des bénéficiaires.
              </p>
              <div className="bg-blue-100 dark:bg-blue-800/30 p-4 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Pour ajouter des informations médicales :</strong><br />
                  1. Complétez le questionnaire d'évaluation<br />
                  2. Utilisez la fonction "Enrichir le profil"<br />
                  3. Les données seront automatiquement synchronisées
                </p>
              </div>
            </div>
          </div>

          {/* Informations complémentaires */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <FileText size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations complémentaires
            </h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Informations supplémentaires</label>
              <textarea
                value={formData.additionalInfo}
                onChange={(e) => handleChange('additionalInfo', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
                placeholder="Informations importantes à connaître"
              />
            </div>
          </div>

          {/* Section Évaluation d'autonomie */}
          <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Activity size={16} className="mr-2 text-purple-600 dark:text-purple-400" />
              Évaluation d'autonomie
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Score d'autonomie (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.autonomyAssessment.autonomyScore}
                  onChange={(e) => handleAutonomyAssessmentChange('autonomyScore', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau GIR</label>
                <select
                  value={formData.autonomyAssessment.gir}
                  onChange={(e) => handleAutonomyAssessmentChange('gir', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner</option>
                  <option value="1">GIR 1</option>
                  <option value="2">GIR 2</option>
                  <option value="3">GIR 3</option>
                  <option value="4">GIR 4</option>
                  <option value="5">GIR 5</option>
                  <option value="6">GIR 6</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Domaines nécessitant de l'aide</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(formData.autonomyAssessment.needsHelp).map(([domain, needsHelp]) => (
                    <div key={domain} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={needsHelp}
                        onChange={(e) => handleNeedsHelpChange(domain, e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:text-purple-400 focus:ring-purple-500 dark:focus:ring-purple-400"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {domain === 'hygiene' && '🧼 Hygiène'}
                        {domain === 'nutrition' && '🍽️ Alimentation'}
                        {domain === 'mobility' && '🚶 Mobilité'}
                        {domain === 'housekeeping' && '🏠 Entretien'}
                        {domain === 'domestic' && '🧹 Activités domestiques'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section Santé et autonomie */}
          <div className="mb-6 p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Heart size={16} className="mr-2 text-pink-600 dark:text-pink-400" />
              Santé et autonomie
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">État général</label>
                <select
                  value={formData.healthSummary.generalHealth}
                  onChange={(e) => handleHealthSummaryChange('generalHealth', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner</option>
                  <option value="Très bon">Très bon</option>
                  <option value="Bon">Bon</option>
                  <option value="Moyen">Moyen</option>
                  <option value="Fragile">Fragile</option>
                  <option value="Précaire">Précaire</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pathologies principales</label>
                <input
                  type="text"
                  value={formData.healthSummary.mainPathologies}
                  onChange={(e) => handleHealthSummaryChange('mainPathologies', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Pathologies connues"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre de médicaments</label>
                <input
                  type="number"
                  min="0"
                  value={formData.healthSummary.medicationCount}
                  onChange={(e) => handleHealthSummaryChange('medicationCount', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Risque de chute</label>
                <select
                  value={formData.healthSummary.fallRisk}
                  onChange={(e) => handleHealthSummaryChange('fallRisk', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner</option>
                  <option value="Faible">Faible</option>
                  <option value="Modéré">Modéré</option>
                  <option value="Élevé">Élevé</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section Réseau de soutien */}
          <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Users size={16} className="mr-2 text-orange-600 dark:text-orange-400" />
              Réseau de soutien
            </h4>

            {/* Contacts d'urgence */}
            <div className="mb-4">
              <h5 className="flex items-center font-semibold text-orange-800 dark:text-orange-300 mb-3">
                <Shield size={16} className="mr-2" />
                Contacts d'urgence
              </h5>
              {formData.emergencyContacts.map((contact, index) => (
                <div key={index} className="bg-orange-100 dark:bg-orange-800/30 p-4 rounded-lg mb-3">
                  <div className="flex justify-between items-center mb-3">
                    <h6 className="font-semibold text-orange-800 dark:text-orange-300">Contact {index + 1}</h6>
                    {formData.emergencyContacts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmergencyContact(index)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => handleEmergencyContactChange(index, 'name', e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Nom complet"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lien</label>
                      <input
                        type="text"
                        value={contact.relationship}
                        onChange={(e) => handleEmergencyContactChange(index, 'relationship', e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Fils, fille, conjoint, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone 1</label>
                      <input
                        type="tel"
                        value={contact.phone1}
                        onChange={(e) => handleEmergencyContactChange(index, 'phone1', e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone 2</label>
                      <input
                        type="tel"
                        value={contact.phone2}
                        onChange={(e) => handleEmergencyContactChange(index, 'phone2', e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addEmergencyContact}
                className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 text-sm font-medium"
              >
                + Ajouter un contact d'urgence
              </button>
            </div>

            {/* Aidant familial */}
            <div>
              <h5 className="font-semibold text-orange-800 dark:text-orange-300 mb-3">Aidant familial</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
                  <input
                    type="text"
                    value={formData.familyCaregiver.name}
                    onChange={(e) => handleFamilyCaregiverChange('name', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nom complet"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lien</label>
                  <input
                    type="text"
                    value={formData.familyCaregiver.relationship}
                    onChange={(e) => handleFamilyCaregiverChange('relationship', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Fils, fille, conjoint, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fréquence</label>
                  <select
                    value={formData.familyCaregiver.frequency}
                    onChange={(e) => handleFamilyCaregiverChange('frequency', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner</option>
                    <option value="Quotidien">Quotidien</option>
                    <option value="Plusieurs fois/semaine">Plusieurs fois/semaine</option>
                    <option value="Hebdomadaire">Hebdomadaire</option>
                    <option value="Occasionnel">Occasionnel</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau de charge</label>
                  <select
                    value={formData.familyCaregiver.burnoutLevel}
                    onChange={(e) => handleFamilyCaregiverChange('burnoutLevel', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner</option>
                    <option value="Faible">Faible</option>
                    <option value="Modéré">Modéré</option>
                    <option value="Élevé">Élevé</option>
                    <option value="Très élevé">Très élevé</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section Besoins en services */}
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Activity size={16} className="mr-2 text-green-600 dark:text-green-400" />
              Besoins en services
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Heures estimées par semaine</label>
                <input
                  type="number"
                  min="0"
                  value={formData.serviceNeeds.estimatedWeeklyHours}
                  onChange={(e) => handleServiceNeedsChange('estimatedWeeklyHours', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
            </div>
          </div>


          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div >
    </div >
  );
};

export default EditClientModal;
